import React, { useState, useEffect } from 'react';
import { Menu, X, MessageCircle, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const CategoryHeader = ({ title = "Medications", whatsappNumber }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'About', href: '#about' },
        { name: 'Products', href: '#products' },
        { name: 'Why Choose Us', href: '#trust' },
        { name: 'Patient Stories', href: '#testimonials' },
        { name: 'FAQ', href: '#faq' },
    ];

    const scrollToSection = (href) => {
        const element = document.querySelector(href);
        if (element) {
            // Offset for sticky header
            const headerOffset = 80; // approximate height of header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            setIsMobileMenuOpen(false);
        }
    };

    const handleWhatsAppClick = () => {
        window.open(`https://wa.me/${whatsappNumber || '919876543210'}`, '_blank');
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
                }`}
        >
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <div
                        className="flex items-center space-x-3 cursor-pointer group"
                        onClick={() => navigate('/')}
                    >
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform duration-200 shadow-md">
                            <span className="text-white font-bold text-2xl">M</span>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">MediSeller</h1>
                            <p className="text-xs text-red-600 font-bold uppercase tracking-wider">{title}</p>
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-6">
                        <button
                            onClick={() => navigate('/')}
                            className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 text-sm"
                        >
                            Home
                        </button>
                        {navLinks.map((link) => (
                            <button
                                key={link.name}
                                onClick={() => scrollToSection(link.href)}
                                className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 text-sm"
                            >
                                {link.name}
                            </button>
                        ))}
                    </nav>

                    {/* CTA Buttons */}
                    <div className="hidden lg:flex items-center space-x-3">
                        <Button
                            onClick={handleWhatsAppClick}
                            className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center space-x-2"
                        >
                            <MessageCircle className="w-4 h-4" />
                            <span>WhatsApp</span>
                        </Button>
                        <Button
                            onClick={() => scrollToSection('#products')}
                            className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg font-semibold transition-all duration-200"
                        >
                            View Products
                        </Button>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="lg:hidden p-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                        {isMobileMenuOpen ? (
                            <X className="w-6 h-6 text-gray-900" />
                        ) : (
                            <Menu className="w-6 h-6 text-gray-900" />
                        )}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="lg:hidden py-4 border-t border-gray-200 bg-white shadow-xl absolute left-0 right-0 px-4">
                        <nav className="flex flex-col space-y-4">
                            <button
                                onClick={() => navigate('/')}
                                className="text-left text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 px-2"
                            >
                                Home
                            </button>
                            {navLinks.map((link) => (
                                <button
                                    key={link.name}
                                    onClick={() => scrollToSection(link.href)}
                                    className="text-left text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 px-2"
                                >
                                    {link.name}
                                </button>
                            ))}
                            <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
                                <Button
                                    onClick={handleWhatsAppClick}
                                    className="bg-green-600 hover:bg-green-700 text-white w-full py-3 rounded-lg font-semibold flex items-center justify-center space-x-2"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    <span>Connect on WhatsApp</span>
                                </Button>
                                <Button
                                    onClick={() => scrollToSection('#products')}
                                    className="bg-red-600 hover:bg-red-700 text-white w-full py-3 rounded-lg font-semibold"
                                >
                                    View Products
                                </Button>
                            </div>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
};

export default CategoryHeader;
